import React, { useEffect, useState } from "react";
import axiosInstance from "../api/axiosInstance";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const pctChange = (curr, prev) => {
  if (prev === 0) return curr === 0 ? 0 : null;
  return ((curr - prev) / prev) * 100;
};

const fmtPct = (pct) => `${pct >= 0 ? "+" : ""}${pct.toFixed(0)}%`;
const fmtNum = (n) => Math.round(n).toLocaleString();
const fmtMoney = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const toDateStr = (d) => d.toISOString().slice(0, 10);

// endpoint: API path returning [{ date: 'YYYY-MM-DD', total: number }, ...]
// kind: "visits" | "income" — controls formatting, wording and color
export default function WeeklyInsights({ endpoint, kind, color = "primary" }) {
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState([]); // last 14 days, oldest first

  const fmt = kind === "income" ? fmtMoney : fmtNum;
  const unitSuffix = kind === "income" ? " BHD" : "";
  const noun = kind === "income" ? "Income" : "Visits";

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axiosInstance.get(endpoint);
        const map = {};
        (res.data?.data || []).forEach((row) => {
          map[row.date] = Number(row.total) || 0;
        });

        const today = new Date();
        const rows = [];
        for (let i = 13; i >= 0; i--) {
          const d = new Date(today);
          d.setDate(d.getDate() - i);
          const key = toDateStr(d);
          rows.push({ date: key, dow: d.getDay(), value: map[key] || 0 });
        }
        setDays(rows);
      } catch (e) {
        console.error("Failed to load weekly insights:", e);
        setDays([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [endpoint]);

  if (loading) {
    return <div className="p-3 text-muted small">Analyzing last 7 days...</div>;
  }

  if (days.length < 14) {
    return (
      <div className="p-3 text-muted small">
        <i className="ti ti-info-circle me-1" />
        Not enough data yet to generate weekly insights.
      </div>
    );
  }

  const lastWeek = days.slice(7, 14);
  const priorWeek = days.slice(0, 7);

  const lastTotal = lastWeek.reduce((s, r) => s + r.value, 0);
  const priorTotal = priorWeek.reduce((s, r) => s + r.value, 0);
  const change = pctChange(lastTotal, priorTotal);
  const avgPerDay = lastTotal / 7;

  const peak = lastWeek.reduce((best, r) => (!best || r.value > best.value ? r : best), null);
  const peakAboveAvg = avgPerDay > 0 ? ((peak.value - avgPerDay) / avgPerDay) * 100 : 0;

  const insights = [
    {
      key: "trend7",
      icon: "ti-trending-up",
      color,
      title: "Last 7 Days vs Prior 7 Days",
      lines: [
        <span key="t">
          {noun} are{" "}
          <b className={change >= 0 ? "text-success" : "text-danger"}>
            {change !== null ? fmtPct(change) : "n/a"}
          </b>{" "}
          vs the previous 7 days ({fmt(priorTotal)}
          {unitSuffix} → {fmt(lastTotal)}
          {unitSuffix}).
        </span>,
        <span key="a" className="text-muted">
          Averaging <b>{fmt(avgPerDay)}{unitSuffix}</b> per day this week.
        </span>,
      ],
    },
    {
      key: "peak7",
      icon: "ti-flame",
      color: kind === "income" ? "success" : "warning",
      title: "Peak Day",
      lines: [
        <span key="p">
          <b>{DAY_NAMES[peak.dow]}</b> was the busiest day this week with{" "}
          <b>{fmt(peak.value)}{unitSuffix}</b>
          {peakAboveAvg > 0 && (
            <> (~{peakAboveAvg.toFixed(0)}% above the daily average)</>
          )}
          .
        </span>,
      ],
    },
  ];

  return (
    <div className="p-3">
      <div className="aiInsightsGrid">
        {insights.map((insight) => (
          <div key={insight.key} className={`aiInsightCard aiInsightCard-${insight.color}`}>
            <div className="aiInsightIcon">
              <i className={`ti ${insight.icon}`} />
            </div>
            <div className="aiInsightBody">
              <div className="aiInsightTitle">{insight.title}</div>
              {insight.lines.map((line, i) => (
                <div className="aiInsightLine" key={i}>{line}</div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
